<template>
  <div :class="classes" @mouseenter="hovering = true" @mouseleave="hovering = false">
    <Transition name="v-slide-in-top">
      <label v-if="labelVisible" class="v-input__label" :for="$slots.input ? undefined : id" :style="{ paddingLeft, paddingRight }">{{ label }}</label>
    </Transition>

    <div style="position: relative; display: flex">
      <span v-if="$slots.prefix" ref="prefix" class="v-input__prefix">
        <slot name="prefix" />
      </span>

      <slot name="input" :style="{ paddingLeft, paddingRight }">
        <input
          :id
          ref="input"
          v-model="model"
          :autocomplete
          class="v-input__inner"
          :disabled="isDisabled"
          :max
          :maxlength
          :min
          :minlength
          :name="name ?? prop ?? undefined"
          :pattern
          :readonly
          :required="isRequired"
          :step
          :style="{ paddingLeft, paddingRight }"
          :tabindex="focusable ? 0 : -1"
          :title="title || label || placeholder || undefined"
          :type
          @blur="onBlur"
          @change="$emit('change', ($event.target as HTMLInputElement).value)"
          @click="$emit('click', $event)"
          @focus="onFocus"
          @input="onInput($event as InputEvent)"
          @keydown="onKeydown"
          @keyup="$emit('keyup', $event)"
        />
      </slot>

      <div
        v-if="placeholderVisible || (focused && remainingSuggestion)"
        class="v-input__value v-input__placeholder"
        :style="{ paddingLeft, paddingRight }"
        @click="focus"
        @mouseup="focus"
      >
        <label v-if="placeholderVisible" :for="$slots.input ? undefined : id">{{ placeholder || label }}</label>
        <template v-else-if="focused && remainingSuggestion">
          <span class="opacity-0">{{ model }}</span>
          <span style="white-space: pre">{{ remainingSuggestion }}</span>
        </template>
      </div>

      <div v-if="$slots.value" class="v-input__value">
        <slot :disabled="isDisabled" name="value" />
      </div>

      <span v-if="hasSuffix" ref="suffix" class="v-input__suffix">
        <Transition name="v-fade-fast">
          <span v-if="clearable && model && !isDisabled && (hovering || focused)" class="v-input__clear v-input__suffix-inner" @click.stop="clear" @mousedown.stop.prevent>
            <VIcon
              class="v-input__icon"
              icon="CloseCircle"
              :size="13"
              :type="status === 'error' || errors.length > 0 ? 'red' : status === 'success' ? 'green' : status ? status : 'info'"
            />
          </span>
        </Transition>

        <slot name="suffix-start" />

        <span v-if="!hideNumberControls && type === 'number' && !isDisabled" class="v-input__number-controls">
          <VButton :debounce="false" :focusable="false" icon="ChevronUp" size="mini" tabindex="-1" type="icon" @click="increment" />
          <VButton :debounce="false" icon="ChevronDown" size="mini" tabindex="-1" type="icon" @click="decrement" />
        </span>

        <span v-if="isRequired" class="v-input__suffix-message">{{ i18n.required }}</span>
        <span v-if="optional" class="v-input__suffix-message">{{ i18n.optional }}</span>
        <span v-if="showCharacterLimit" class="v-input__length" v-text="characterLimitText" />

        <VIconHelp
          class="v-input__suffix-inner"
          :help
          icon-class="v-input__icon"
          :type="status === 'error' || errors.length > 0 ? 'red' : status === 'success' ? 'green' : status ? status : 'placeholder'"
        />

        <slot name="suffix" />
      </span>
      <Transition name="v-fade">
        <VLoadingBar v-if="loading" class="v-input__loading" style="margin-top: -3px" />
      </Transition>
    </div>

    <slot name="below" />

    <VErrors :errors />

    <div v-if="suggestions.length > 0" class="v-input__suggestions">
      <VButton v-for="suggest in suggestions" :key="suggest" :label="suggest" size="small" type="secondary" @click="onClickSuggestion(suggest)" />
    </div>
  </div>
</template>

<script setup lang="ts" generic="TModel extends string | number | null">
import { get } from 'lodash-es'
import { computed, inject, onMounted, ref, useId, useSlots, useTemplateRef, watch } from 'vue'

import { useClassName } from '../composables/useClassName.ts'
import { useErrors } from '../composables/useErrors.ts'
import { useResizeObserver } from '../composables/useResizeObserver.ts'
import type { VInputSize, VInputStatus, VInputType } from '../types.ts'
import { nextFrame } from '../utils/helpers.ts'
import { i18nInput as i18n } from '../utils/i18n.ts'
import { $formKey } from '../utils/injections.ts'
import VIcon from './Icon/VIcon.vue'
import VButton from './VButton.vue'
import VErrors from './VErrors.vue'
import VIconHelp from './VIconHelp.vue'
import VLoadingBar from './VLoadingBar.vue'

export interface Props {
  /**
   * Same as HTML `input` type attribute.
   */
  type?: VInputType

  /**
   * Same as HTML `input` autocomplete attribute.
   */
  autocomplete?: string

  /**
   * The placeholder to display when no value is given.
   */
  placeholder?: string

  /**
   * The label to display. Acts as a placeholder when no value is given, and is shown above the input value when a value is given.
   */
  label?: string

  /**
   * Same as HTML `input` disabled attribute.
   */
  disabled?: boolean | null

  /**
   * Adds a clear button which clears the input value.
   */
  clearable?: boolean

  /**
   * Same as HTML `input` readonly attribute.
   */
  readonly?: boolean

  /**
   * Sets the tab index to 0 which allows the input to be focused with the `Tab` key.
   */
  focusable?: boolean

  /**
   * Same as HTML `input` min attribute.
   */
  min?: string

  /**
   * Same as HTML `input` max attribute.
   */
  max?: string

  /**
   * Same as HTML `input` maxlength attribute.
   */
  maxlength?: number

  /**
   * Same as HTML `input` minlength attribute.
   */
  minlength?: number

  /**
   * Same as HTML `input` step attribute.
   */
  step?: string

  /**
   * Changes the border color of the input.
   */
  status?: VInputStatus

  /**
   * Displays one or more error messages under the input.
   */
  error?: string | string[]

  /**
   * Displays one or more help messages in a tooltip by hovering over a help icon inside the input.
   */
  help?: string | string[]

  /**
   * Displays a suggestion inside the input when typing as greyed out text.
   * For example when your suggestion is `John Doe` and the user typed `John` it will suggest ` Doe`.
   */
  suggestion?: string | number

  /**
   * Displays a multiple suggestions under the input as buttons that can be clicked.
   */
  suggestions?: string[]

  /**
   * Same as HTML `input` pattern attribute.
   */
  pattern?: string

  /**
   * Can be used in combination with `VForm` to get automatic validation of the input value.
   * Should be the `key` in the form object that matches the model value if this input.
   */
  prop?: string

  /**
   * Same as HTML `input` name attribute.
   */
  name?: string | null

  /**
   * Same as HTML `input` title attribute.
   */
  title?: string | null

  /**
   * Shows a `required` label inside the input.
   */
  required?: boolean | null

  /**
   * Trims the model value.
   */
  trim?: boolean

  /**
   * Hides all normal styling of the input. Can be used in for example an editable table cell.
   */
  minimal?: boolean

  /**
   * Displays the current length and max length of the input inside the input.
   */
  showCharacterLimit?: boolean

  /**
   * The size of the input.
   */
  size?: VInputSize

  /**
   * Shows an `optional` label inside the input.
   */
  optional?: boolean

  /**
   * Will focus the input when it is mounted.
   */
  focusOnMount?: boolean

  /**
   * Shows an indeterminate loading bar under the input.
   */
  loading?: boolean

  /**
   * Hides the number controls for number inputs.
   */
  hideNumberControls?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  autocomplete: 'off',
  placeholder: '',
  label: '',
  disabled: null,
  clearable: false,
  readonly: false,
  focusable: true,
  min: undefined,
  max: undefined,
  maxlength: undefined,
  step: '1',
  minlength: undefined,
  status: '',
  error: '',
  help: '',
  suggestion: '',
  suggestions: () => [],
  pattern: undefined,
  prop: '',
  name: null,
  title: null,
  required: null,
  trim: false,
  minimal: false,
  showCharacterLimit: false,
  size: 'small',
  optional: false,
  focusOnMount: false,
  loading: false,
})

const emit = defineEmits<{
  input: [value: any, event: InputEvent]
  keydown: [value: KeyboardEvent]
  keyup: [value: KeyboardEvent]
  click: [value: MouseEvent]
  change: [value: string]
  clear: []
  blur: [value: FocusEvent]
  focus: [value: FocusEvent]
}>()

defineSlots<{
  below?: (props: any) => any
  input?: (props: any) => any
  prefix?: (props: any) => any
  suffix?: (props: any) => any
  'suffix-start'?: (props: any) => any
  value?: (props: any) => any
}>()

const slots = useSlots()
const form = inject($formKey, undefined)

const id = useId()

/**
 * The value of the input.
 */
const model = defineModel<TModel>({ default: null })
const hovering = ref(false)
const focused = ref(false)
const suffix = useTemplateRef('suffix')
const suffixWidth = ref(20)
const prefix = useTemplateRef('prefix')
const prefixWidth = ref(0)
const paddingLeft = computed(() => `${prefixWidth.value + (prefixWidth.value ? 5 : realSize.value === 'small' ? 10 : 20)}px`)
const paddingRight = computed(() => `${suffixWidth.value + (realSize.value === 'small' ? 5 : 10)}px`)

const hasInput = computed(() => model.value || model.value === 0)
const hasSuffix = computed(
  () => props.clearable || isRequired.value || props.optional || slots.suffix || slots['suffix-start'] || characterLimitText.value || props.help || props.type === 'number',
)
const labelVisible = computed(() => props.label && (hasInput.value || (props.suggestion && focused.value)))
const placeholderVisible = computed(() => !hasInput.value && (props.placeholder || props.label) && (!props.suggestion || !focused.value))
const remainingSuggestion = computed((): string => {
  const value = String(model.value ?? '')
  const index = value.length
  const trimmedLength = value.trimEnd().length

  return `${props.suggestion}`.slice(index - (index - trimmedLength))
})
const isDisabled = computed(() => !!(props.disabled || (props.disabled === null && form?.props?.disabled)))
const isRequired = computed(() => {
  if (props.required) {
    return true
  }

  if (props.required === null && props.prop) {
    const rules = get(form?.props?.rules, props.prop, {})

    return 'required' in rules && rules.required
  }

  return false
})

const patternRegExp = computed((): undefined | RegExp => (props.pattern ? new RegExp(props.pattern) : undefined))

const realSize = computed(() => (props.label ? 'large' : props.size))

const classes = useClassName(
  'v-input',
  computed(() => [props.label, props.placeholder]),
  computed(() => ({
    'has-suffix': hasSuffix.value,
    'has-prefix': !!slots.prefix,
    'has-label': props.label,
    'has-input': !!hasInput.value,
    'has-suggestion': focused.value && props.suggestion,
    'is-minimal': props.minimal,
    'is-disabled': isDisabled.value,
    'is-required': isRequired.value,
    'is-optional': props.optional,
    focused: focused.value,
    hovering: hovering.value,
    [`v-input--${realSize.value}`]: realSize.value,
    [`v-input--${props.status}`]: props.status,
    'v-input--error': props.status === 'error' || errors.value.length > 0,
  })),
)

const characterLimitText = computed(() => {
  if (!props.showCharacterLimit || props.maxlength === undefined) return ''

  const length = model.value ? String(model.value).length : 0

  return `${length} / ${props.maxlength}`
})

const errors = useErrors(props)

watch(
  () => props.prop,
  () => (props.prop ? form?.validateOneProp(props.prop) : undefined),
)

useResizeObserver(suffix, (_, target) => {
  suffixWidth.value = target.clientWidth
})

useResizeObserver(prefix, (_, target) => {
  prefixWidth.value = target.clientWidth
})

onMounted(async () => {
  if (props.focusOnMount) {
    await nextFrame()
    focus()
  }
})

const input = useTemplateRef('input')

function focus() {
  input.value?.focus()
}

function onFocus(event: FocusEvent) {
  focused.value = true
  emit('focus', event)
}

function onBlur(event: FocusEvent) {
  focused.value = false
  emit('blur', event)

  if (props.prop) {
    form?.validateOneProp(props.prop, 'blur')
  }
}

function onKeydown(event: KeyboardEvent) {
  emit('keydown', event)
  if (props.prop) {
    form?.validateOneProp(props.prop, 'keyup')
  }
}

function clear() {
  if (props.type === 'number') {
    model.value = null as TModel
  } else {
    model.value = '' as TModel
  }
  emit('clear')

  if (props.prop) {
    form?.validateOneProp(props.prop)
  }

  // focused should be set to false, since blur won't be called when focused prop is set manually.
  // Which can happen in VSelect component with filterable = true.
  focused.value = false
}

function onInput(event: InputEvent) {
  // Number inputs don't go well with custom input handling. (Decimals don't work)
  if (props.type === 'number') {
    return
  }

  let value = (event.target as HTMLInputElement)?.value

  if (props.trim) {
    value = value.trim()
  }

  // If a pattern is given, make sure the entire value matches the pattern.
  if (value && patternRegExp.value) {
    const match = value.match(patternRegExp.value)
    if (!match || match[0] !== match.input) {
      value = String(model.value ?? '')
    }
  }

  if (input.value) {
    input.value.value = value
  }

  model.value = value as TModel
  emit('input', value, event)
}

function onClickSuggestion(suggestion: string) {
  model.value = suggestion as TModel
  focus()
}

function increment() {
  const value = formatNumber(Number(model.value) + Number(props.step))
  model.value = value as TModel
  focus()
}

function decrement() {
  const value = formatNumber(Number(model.value) - Number(props.step))
  model.value = value as TModel
  focus()
}

const min = computed(() => (props.min === undefined ? undefined : Number(props.min)))
const max = computed(() => (props.max === undefined ? undefined : Number(props.max)))

function formatNumber(value: number): number {
  if (min.value !== undefined && value < min.value) value = min.value
  if (max.value !== undefined && value > max.value) value = max.value

  let formatted = String(value)

  const split = props.step.split('.')

  if (split.length === 2) {
    formatted = value.toFixed(split[1].length)
  } else if (split.length === 1) {
    formatted = value.toFixed(0)
  }

  return Number(formatted)
}

defineExpose({
  characterLimitText,
  clear,
  focus,
  focused,
  form,
  hovering,
  errors,
  input,
})
</script>

<style>
.v-input {
  position: relative;
  width: 100%;
  pointer-events: none;

  &.v-input--medium .v-input__inner,
  &.v-input--medium .v-input__value {
    font-size: 16px;
    min-height: 50px;
    padding: 8px 20px;
  }

  &.v-input--large .v-input__inner,
  &.v-input--large .v-input__value {
    font-size: 20px;
    min-height: 60px;
    padding: 12px 20px;
  }

  &.hovering > div > .v-input__inner {
    border-color: var(--color-text-placeholder);
  }

  &.focused:not(.is-minimal) > div > .v-input__inner {
    border-color: var(--color-primary);
  }

  &.is-disabled:not(.is-minimal) {
    .v-input__inner {
      background-color: #fafafa;
    }

    .v-input__suffix {
      background-color: #fafafa;
      box-shadow: -10px 0px 5px -5px rgb(250 250 250 / 90%);
    }
  }

  &.is-minimal > div > .v-input__inner {
    background: none;
    border-color: transparent;
    box-shadow: none;
  }

  &.v-input--medium .v-input__prefix,
  &.v-input--large .v-input__prefix {
    padding-left: 20px;
  }

  &.v-input--medium .v-input__suffix,
  &.v-input--large .v-input__suffix {
    padding-right: 20px;
  }

  &.v-input--success > div > .v-input__inner {
    border-color: var(--color-green);
  }

  &.v-input--error > div > .v-input__inner {
    border-color: var(--color-red);
  }

  &.has-label.has-input,
  &.has-label.has-suggestion {
    .v-input__inner,
    .v-input__value {
      padding-top: 21px;
      padding-bottom: 0px;
    }

    .v-input__prefix {
      padding-top: 20px;
    }
  }
}

.v-input__inner,
.v-input__value {
  padding: 5px 10px;
  width: 100%;
  min-height: 36px;
  font-size: 16px;
  font-family: var(--font-family);
  font-weight: 400;
}

.v-input__inner {
  background-color: #fff;
  border: var(--border);
  border-radius: var(--border-radius);
  transition:
    border var(--transition),
    padding var(--transition),
    box-shadow var(--transition);
  outline: none;
  pointer-events: auto;

  &::placeholder {
    color: var(--color-text-placeholder);
    font-weight: 400;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }

  /* Firefox */
  &[type='number'] {
    appearance: textfield;
  }
}

.v-input__inner:is(:-webkit-autofill, :autofill) + .v-input__value {
  color: transparent;
}

.v-input__value {
  display: flex;
  align-items: center;
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  white-space: nowrap;
  overflow-x: hidden;
  transition: padding var(--transition);
  cursor: text;
}

.v-input__label {
  display: flex;
  justify-content: space-between;
  align-items: start;
  position: absolute;
  width: 100%;
  font-size: 12px;
  left: 0;
  top: 10px;
  line-height: 0.8;
  z-index: 1;
}

.v-input__placeholder {
  color: var(--color-text-placeholder);
  position: absolute;
  user-select: none;
  margin-left: 1px;

  label {
    text-overflow: ellipsis;
    overflow: hidden;
  }
}

.v-input__prefix {
  position: absolute;
  padding-left: 10px;
  bottom: 2px;
  top: 2px;
  text-align: center;
  color: var(--color-text-placeholder);
  transition: all 0.3s;
  pointer-events: none;
  display: flex;
  align-items: center;
  z-index: 1;
}

.v-input__suffix {
  position: absolute;
  padding-right: 8px;
  bottom: 1px;
  top: 1px;
  right: 1px;
  border-radius: var(--border-radius);
  text-align: center;
  color: var(--color-text-placeholder);
  font-size: 14px;
  transition: all 0.3s;
  pointer-events: none;
  display: flex;
  align-items: center;
  background-color: transparent;
  z-index: 1;

  .v-input__suffix-inner {
    pointer-events: all;
    display: inline-flex;
    align-items: center;
    padding: 0 2px;
  }
}

.v-input__length {
  padding: 0 2px;
  white-space: nowrap;
}

.v-input__suffix-message {
  color: var(--color-text-placeholder);
  font-style: italic;
  padding: 0 2px;
}

.v-input__suggestions {
  pointer-events: all;
  margin-top: 10px;

  .v-btn {
    margin-bottom: 5px;
  }
}

.v-input--success > div > .v-input__suffix,
.v-input--success > div > .v-input__label {
  color: var(--color-green);
}

.v-input--error > div > .v-input__suffix,
.v-input--error > div > .v-input__label {
  color: var(--color-red);
}

.v-input__icon {
  width: 25px;
  line-height: 36px;
  transition: all var(--transition);
  padding: 0 2px;
}

.v-input__clear {
  cursor: pointer;
  position: absolute;
  left: -21px;
  background-color: #fff;
}

.v-input__loading {
  position: absolute;
  bottom: 0;
  left: 1px;
  margin-top: 0px !important;
  margin-bottom: 0px !important;
  width: calc(100% - 2px);
}

.v-input__number-controls {
  display: flex;
  flex-direction: column;
  margin-right: 1px;
  pointer-events: all;

  .v-btn {
    margin: -1px 0;
  }
}

.v-input.has-label .v-input__number-controls .v-btn {
  margin: 3px 0;
}

.v-input__suffix .v-btn,
.v-input__prefix .v-btn {
  pointer-events: all;
}
</style>
